import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, Building2, ChevronRight,
  CheckCircle, Clock, AlertTriangle, Eye, Plus, CreditCard, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_CONFIG = {
  protected: { icon: ShieldCheck, color: "#556B2F", bg: "bg-[#556B2F]/10", border: "border-[#556B2F]/30", label: "Protected" },
  under_review: { icon: Clock, color: "#F59E0B", bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]/30", label: "Under Review" },
  high_risk: { icon: ShieldAlert, color: "#ef4444", bg: "bg-red-400/10", border: "border-red-400/30", label: "High Risk" },
  unprotected: { icon: ShieldX, color: "#71717A", bg: "bg-white/5", border: "border-white/10", label: "Unprotected" },
};

const PLAN_COLORS = { basic: "#71717A", standard: "#D4AF37", premium: "#556B2F" };

export default function EZAssureDashboard() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showActivate, setShowActivate] = useState(false);
  const [selectedProp, setSelectedProp] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [paymentStep, setPaymentStep] = useState("select"); // select | review | processing | success
  const [currentOrder, setCurrentOrder] = useState(null);
  const [paying, setPaying] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, plRes, pRes] = await Promise.all([
        fetch(`${API}/ezassure/subscriptions`, { credentials: "include" }),
        fetch(`${API}/ezassure/plans`, { credentials: "include" }),
        fetch(`${API}/properties`, { credentials: "include" }),
      ]);
      if (sRes.ok) setSubs(await sRes.json());
      if (plRes.ok) setPlans(await plRes.json());
      if (pRes.ok) setProperties(await pRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activate = async () => {
    if (!selectedProp || !selectedPlan) return;
    setPaying(true);
    setPaymentStep("processing");
    try {
      // Step 1: Create payment order
      const orderRes = await fetch(`${API}/payments/create-order`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ property_id: selectedProp, plan_id: selectedPlan }),
      });
      if (!orderRes.ok) { setPaying(false); setPaymentStep("select"); return; }
      const order = await orderRes.json();
      setCurrentOrder(order);

      // Step 2: Simulate Razorpay payment (replace with real Razorpay checkout)
      // In production: const rzp = new Razorpay({key: order.key_id, amount: order.amount_paise, ...})
      await new Promise(r => setTimeout(r, 2000)); // Simulate payment processing

      // Step 3: Verify payment
      const verifyRes = await fetch(`${API}/payments/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ order_id: order.order_id, razorpay_payment_id: `pay_sim_${Date.now()}`, razorpay_signature: "simulated" }),
      });
      if (verifyRes.ok) {
        setPaymentStep("success");
        fetchData();
      } else {
        setPaymentStep("select");
      }
    } catch (e) {
      console.error(e);
      setPaymentStep("select");
    }
    setPaying(false);
  };

  const protectedCount = subs.filter(s => s.assure_status === "protected").length;
  const reviewCount = subs.filter(s => s.assure_status === "under_review").length;
  const unprotectedProps = properties.filter(p => !subs.find(s => s.property_id === p.property_id && s.status === "active"));

  return (
    <div data-testid="ezassure-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-serif text-2xl text-white">eZAssure</h2>
          </div>
          <p className="text-white/40 text-sm font-sans">Title Protection & Legal Risk Assurance</p>
        </div>
        <Button data-testid="activate-ezassure-btn" onClick={() => setShowActivate(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
          <Plus className="w-4 h-4 mr-2" /> Activate Protection
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Protected", value: protectedCount, color: "#556B2F", icon: ShieldCheck },
          { label: "Under Review", value: reviewCount, color: "#F59E0B", icon: Clock },
          { label: "Unprotected", value: unprotectedProps.length, color: "#71717A", icon: ShieldX },
          { label: "Active Plans", value: subs.filter(s => s.status === "active").length, color: "#D4AF37", icon: Shield },
        ].map(s => (
          <div key={s.label} className="bg-[#11130F] border border-white/10 rounded-sm p-4">
            <s.icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
            <p className="font-serif text-2xl text-white">{s.value}</p>
            <p className="text-white/40 text-[10px] font-sans uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[{ key: "overview", label: "Protected Properties" }, { key: "plans", label: "Available Plans" }].map(t => (
          <button key={t.key} data-testid={`ezassure-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors ${activeTab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/50 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <>
          {subs.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Shield className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans mb-4">No properties under eZAssure protection yet.</p>
              <Button onClick={() => setShowActivate(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans text-xs">
                Activate Your First Protection
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {subs.map(sub => {
                const st = STATUS_CONFIG[sub.assure_status] || STATUS_CONFIG.unprotected;
                return (
                  <div key={sub.subscription_id}
                    onClick={() => navigate(`/dashboard/ezassure/${sub.property_id}`)}
                    className={`bg-[#11130F] border ${st.border} rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors cursor-pointer group`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 ${st.bg} rounded-sm flex items-center justify-center`}>
                          <st.icon className="w-5 h-5" style={{ color: st.color }} />
                        </div>
                        <div>
                          <h4 className="font-sans font-medium text-white text-sm">{sub.property?.name || sub.property_id}</h4>
                          <p className="text-white/40 text-xs font-sans">{sub.property?.address}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-[10px] font-sans" style={{ color: st.color }}>{st.label}</span>
                            <span className="text-white/20">|</span>
                            <span className="text-[10px] font-sans" style={{ color: PLAN_COLORS[sub.plan_id] }}>{sub.plan_name}</span>
                            <span className="text-white/20">|</span>
                            <span className="text-white/30 text-[10px] font-sans">{sub.monitoring_frequency} monitoring</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="hidden sm:flex flex-wrap gap-1">
                          {Object.entries(sub.coverage || {}).filter(([, v]) => v).slice(0, 3).map(([k]) => (
                            <span key={k} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#D4AF37]/10 text-[#D4AF37] font-sans">
                              {k.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                        <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "plans" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map(plan => (
            <div key={plan.plan_id} className={`bg-[#11130F] border rounded-sm p-6 ${plan.plan_id === "premium" ? "border-[#D4AF37]/40" : "border-white/10"}`}>
              {plan.plan_id === "premium" && (
                <span className="text-[10px] bg-[#D4AF37] text-black px-2 py-0.5 rounded-sm font-sans font-bold uppercase mb-3 inline-block">Recommended</span>
              )}
              <h3 className="font-serif text-xl text-white mb-1">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-serif text-3xl" style={{ color: PLAN_COLORS[plan.plan_id] || "#D4AF37" }}>
                  {plan.price.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                </span>
                <span className="text-white/30 text-xs font-sans">/{plan.period}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs font-sans">
                    <CheckCircle className="w-3 h-3 text-[#D4AF37] mt-0.5 shrink-0" />
                    <span className="text-white/60">{f}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => { setSelectedPlan(plan.plan_id); setShowActivate(true); }}
                className={`w-full rounded-sm font-sans text-xs ${plan.plan_id === "premium" ? "bg-[#D4AF37] text-black hover:bg-[#F59E0B]" : "bg-white/5 border border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                Select Plan
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={showActivate} onOpenChange={(open) => { setShowActivate(open); if (!open) { setPaymentStep("select"); setCurrentOrder(null); } }}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {paymentStep === "success" ? "Payment Successful" : paymentStep === "processing" ? "Processing Payment" : "Activate eZAssure"}
            </DialogTitle>
          </DialogHeader>

          {paymentStep === "select" && (
            <div className="space-y-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Property</label>
                <Select value={selectedProp} onValueChange={setSelectedProp}>
                  <SelectTrigger data-testid="ezassure-property-select" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {properties.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Plan</label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger data-testid="ezassure-plan-select" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {plans.map(p => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name} — {p.price.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}/yr</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selectedPlan && (
                <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-2">Order Summary</p>
                  <div className="flex justify-between text-sm font-sans mb-1">
                    <span className="text-white/50">{plans.find(p => p.plan_id === selectedPlan)?.name}</span>
                    <span className="text-white">{plans.find(p => p.plan_id === selectedPlan)?.price.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between text-xs font-sans text-white/30">
                    <span>Duration</span><span>1 Year</span>
                  </div>
                  <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-sm font-sans">
                    <span className="text-white/60">Total</span>
                    <span className="text-[#D4AF37] font-medium">{plans.find(p => p.plan_id === selectedPlan)?.price.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
              )}
              <Button data-testid="confirm-ezassure-btn" onClick={() => setPaymentStep("review")} disabled={!selectedProp || !selectedPlan} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
                <CreditCard className="w-4 h-4 mr-2" /> Proceed to Payment
              </Button>
              <p className="text-white/20 text-[10px] font-sans text-center">Simulated Razorpay — replace with real keys for production</p>
            </div>
          )}

          {paymentStep === "review" && (
            <div className="space-y-4">
              <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm p-4 text-center">
                <CreditCard className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-white font-sans text-sm mb-1">Razorpay Checkout</p>
                <p className="text-white/40 text-xs font-sans">Amount: <strong className="text-[#D4AF37]">{plans.find(p => p.plan_id === selectedPlan)?.price.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</strong></p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-sm p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs font-sans text-white/50">
                  <div className="w-4 h-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"><CheckCircle className="w-3 h-3 text-[#D4AF37]" /></div>
                  UPI / Card / Net Banking
                </div>
                <div className="flex items-center gap-2 text-xs font-sans text-white/50">
                  <div className="w-4 h-4 rounded-full bg-[#556B2F]/20 flex items-center justify-center"><Shield className="w-3 h-3 text-[#556B2F]" /></div>
                  Secure & Encrypted Payment
                </div>
              </div>
              <Button data-testid="pay-now-btn" onClick={activate} disabled={paying} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm">
                {paying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : "Pay Now (Simulated)"}
              </Button>
              <button onClick={() => setPaymentStep("select")} className="w-full text-white/30 text-xs font-sans hover:text-white/50 transition-colors">Back</button>
            </div>
          )}

          {paymentStep === "processing" && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
              <p className="text-white font-sans text-sm">Processing your payment...</p>
              <p className="text-white/40 text-xs font-sans mt-1">Please wait while we verify with Razorpay</p>
            </div>
          )}

          {paymentStep === "success" && (
            <div className="text-center py-4 space-y-4">
              <div className="w-14 h-14 bg-[#556B2F]/20 border border-[#556B2F]/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-[#556B2F]" />
              </div>
              <div>
                <p className="text-white font-sans text-base font-medium">Payment Successful</p>
                <p className="text-white/40 text-xs font-sans mt-1">eZAssure protection is now active for your property</p>
              </div>
              {currentOrder && (
                <div className="bg-white/5 border border-white/10 rounded-sm p-3 text-left">
                  <div className="flex justify-between text-xs font-sans mb-1"><span className="text-white/40">Plan</span><span className="text-white">{currentOrder.plan_name}</span></div>
                  <div className="flex justify-between text-xs font-sans mb-1"><span className="text-white/40">Amount</span><span className="text-[#D4AF37]">{currentOrder.amount?.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span></div>
                  <div className="flex justify-between text-xs font-sans"><span className="text-white/40">Receipt</span><span className="text-white/60">{currentOrder.receipt}</span></div>
                </div>
              )}
              <Button data-testid="payment-done-btn" onClick={() => { setShowActivate(false); setPaymentStep("select"); setCurrentOrder(null); setSelectedPlan(""); setSelectedProp(""); }} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
